"use client"

import Image from "next/image"
import { useQuery } from "@tanstack/react-query"
import { CalendarDays, CloudCog, Star } from "lucide-react"

import {
  getRestaurantDetail,
  getRestaurantRating,
  getRestaurantTiming,
} from "@/lib/client/api/restaurant"
import { cn, formatRatingCount } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Icons } from "@/components/icons"

interface RestaurantInfoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  restaurantId: number
}

// Define the API response structure
interface TimingSlot {
  time_id: number
  start_time: string
  end_time: string
  category_list: any[]
  item_list: any[]
}

interface DayTiming {
  day_id: number
  name: string
  timings: TimingSlot[]
}

// interface Rating {
//   user_name: string
//   user_image: string
//   rated_at: string
//   rating: number
//   feedback: string
//   like_items: any[]
//   images: string[]
//   rating_id: number
//   visibility: boolean
//   deleted_by: string
//   order_id: number
//   merchant_feedback: string
//   merchant_replied_at: string
//   dislike_items: any[]
//   feedbacks: any[]
// }

export function RestaurantInfoDialog({
  open,
  onOpenChange,
  restaurantId,
}: RestaurantInfoDialogProps) {
  // Get today's day number (0-6, with 0 being Sunday)
  const today = new Date().getDay()
  // Convert to the format used by the API (1-7, with Monday as 1)
  const apiToday = today === 0 ? 1 : today + 1

  // Fetch restaurant timings data
  const { data: timingsData, isLoading: isLoadingTimings } = useQuery({
    queryKey: ["restaurantTiming", restaurantId],
    queryFn: () =>
      getRestaurantTiming(restaurantId).then((response) => response),
    enabled: open, // Only fetch when dialog is open
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  })

  // Fetch restaurant info data
  const { data: restaurantData, isLoading: isLoadingRestaurantInfo } = useQuery(
    {
      queryKey: ["restaurant", restaurantId],
      queryFn: () => getRestaurantDetail(restaurantId),
      enabled: open, // Only fetch when dialog is open
    }
  )

  // // Fetch restaurant ratings data
  const { data: ratingsData } = useQuery({
    queryKey: ["restaurantRatings", restaurantId],
    queryFn: () => getRestaurantRating(restaurantId),
    enabled: open, // Only fetch when dialog is open
  })

  // // Calculate rating distribution
  // const getRatingDistribution = () => {
  //   if (!ratingsData?.ratings || !ratingsData?.ratings.length) {
  //     return null
  //   }

  //   // Initialize counts for each rating (1-5)
  //   const distribution: Record<string, number> = {
  //     "1": 0,
  //     "2": 0,
  //     "3": 0,
  //     "4": 0,
  //     "5": 0,
  //   }

  //   // Count ratings
  //   ratingsData.ratings.forEach((rating: Rating) => {
  //     const score = String(rating.rating)
  //     if (distribution[score] !== undefined) {
  //       distribution[score]++
  //     }
  //   })

  //   return distribution
  // }

  // Format opening hours for display
  const formatOpeningHours = (time: string) => {
    if (!time) return ""
    // If time is in format "HH:MM:SS", remove the seconds
    return time.length === 8 ? time.substring(0, 5) : time
  }

  // Get rating distribution data
  // const ratingDistribution = getRatingDistribution()
  // const totalRatingCount = ratingsData?.ratings?.length || 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 max-h-[80vh] overflow-hidden">
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle>Thông tin nhà hàng</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-full max-h-[calc(80vh-60px)]">
          <div className="p-5">
            {/* Restaurant Header */}
            <div className="bg-white rounded-lg mb-4">
              {isLoadingRestaurantInfo ? (
                <>
                  {/* Restaurant Header Skeleton */}
                  <div className="flex items-center gap-1 mb-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-8 w-3/4 mb-2" />
                  <div className="flex items-start gap-2 mb-4">
                    <Skeleton className="h-5 w-5 rounded-full mt-0.5 shrink-0" />
                    <Skeleton className="h-5 w-full" />
                  </div>

                  {/* Rating Skeleton */}
                  <div className="py-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col items-center">
                        <Skeleton className="h-8 w-16 mb-1" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <div className="flex-1 ml-6">
                        {[...Array(5)].map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 mb-1"
                          >
                            <Skeleton className="h-4 w-2" />
                            <Skeleton className="h-2 w-full rounded-full" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Tier Badge Skeleton */}
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                </>
              ) : restaurantData?.data ? (
                <>
                  {restaurantData.data.restaurant_info.tier && (
                    <div className="flex items-center gap-1 mb-2">
                      {restaurantData.data.restaurant_info.tier.logo && (
                        <Image
                          src={restaurantData.data.restaurant_info.tier.logo}
                          alt="Tier"
                          width={16}
                          height={16}
                          className="h-4 w-4"
                        />
                      )}
                      <span className="text-xs text-gray-600">Đối tác</span>
                    </div>
                  )}

                  <h2 className="text-xl font-semibold mb-2">
                    {restaurantData.data.restaurant_info.name}
                  </h2>

                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2 flex-1">
                      <Icons.mapPin className="text-gray-500 size-5" />
                      <span className="text-sm text-gray-600 line-clamp-2">
                        {restaurantData.data.restaurant_info.address}
                      </span>
                    </div>
                  </div>

                  {/* Restaurant Rating */}
                  {/* {(restaurantData.data.restaurant_info.rating > 0 ||
                    isLoadingRatings) && (
                    <div className="flex items-center justify-between py-4 border-t border-gray-100">
                      <div className="flex flex-col items-center">
                        <div className="text-3xl font-bold text-gray-900 flex items-center gap-1">
                          {restaurantData.data.restaurant_info.rating.toFixed(
                            1
                          )}
                          <Star size={20} className="fill-gray-900" />
                        </div>
                        <span className="text-xs text-blue-600">
                          {totalRatingCount ||
                            restaurantData.data.restaurant_info
                              .review_count}{" "}
                          đánh giá
                        </span>
                      </div>

                      <div className="flex-1 ml-6">
                        {isLoadingRatings
                          ? // Rating skeleton while loading
                            [...Array(5)].map((_, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 mb-1"
                              >
                                <Skeleton className="h-4 w-2" />
                                <Skeleton className="h-2 w-full rounded-full" />
                                <Skeleton className="h-4 w-8" />
                              </div>
                            ))
                          : ratingDistribution
                            ? // Show distribution from ratings API
                              [5, 4, 3, 2, 1].map((rating) => {
                                const count =
                                  ratingDistribution[String(rating)] || 0
                                const percentage =
                                  totalRatingCount > 0
                                    ? (count / totalRatingCount) * 100
                                    : 0

                                return (
                                  <div
                                    key={rating}
                                    className="flex items-center gap-2 mb-1"
                                  >
                                    <span className="text-xs text-gray-500 w-2">
                                      {rating}
                                    </span>
                                    <div className="h-2 bg-gray-200 flex-1 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-amber-500 rounded-full"
                                        style={{
                                          width: `${percentage}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-600 min-w-8 text-right">
                                      {count}
                                    </span>
                                  </div>
                                )
                              })
                            : // Fallback to existing rating distribution from restaurant data
                              [5, 4, 3, 2, 1].map((rating) => (
                                <div
                                  key={rating}
                                  className="flex items-center gap-2 mb-1"
                                >
                                  <span className="text-xs text-gray-500 w-2">
                                    {rating}
                                  </span>
                                  <div className="h-2 bg-gray-200 flex-1 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-amber-500 rounded-full"
                                      style={{
                                        width: `${
                                          restaurantData.data.restaurant_info
                                            .review_count > 0
                                            ? ((restaurantData.data
                                                .rating_distribution?.[
                                                rating.toString()
                                              ] || 0) /
                                                restaurantData.data
                                                  .restaurant_info
                                                  .review_count) *
                                              100
                                            : 0
                                        }%`,
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-600 min-w-8 text-right">
                                    {restaurantData.data.rating_distribution?.[
                                      rating.toString()
                                    ] || 0}
                                  </span>
                                </div>
                              ))}
                      </div>
                    </div>
                  )} */}

                  {/* Restaurant Badges/Tier */}
                  {restaurantData.data.restaurant_info.tier && (
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                      <Badge
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        variant="secondary"
                      >
                        {restaurantData.data.restaurant_info.tier.name}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Nhà hàng cao cấp
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  Không có thông tin nhà hàng
                </div>
              )}
            </div>

            {/* Ratings */}
            <div className="bg-white rounded-lg mt-8">
              <h3 className="text-lg font-semibold mb-4">Đánh giá</h3>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2 flex-1">
                  <Star className="text-gray-500 size-5" />
                  <span className="text-sm text-gray-600 line-clamp-2">
                    {ratingsData?.restaurant?.total_rating} (
                    {formatRatingCount(
                      ratingsData?.restaurant?.review_count || 0
                    )}{" "}
                    đánh giá)
                  </span>
                </div>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-white rounded-lg mt-8">
              <h3 className="text-lg font-semibold mb-4">Giờ hoạt động</h3>

              {(() => {
                if (isLoadingTimings) {
                  return (
                    <div className="space-y-2">
                      {[...Array(7)].map((_, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-3"
                        >
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-4 w-4 rounded-full" />
                            <Skeleton className="h-4 w-20" />
                          </div>
                          <Skeleton className="h-4 w-24" />
                        </div>
                      ))}
                    </div>
                  )
                }

                // Get timing data from different possible API response structures
                const timings =
                  timingsData?.timings || timingsData?.data?.timings

                if (timings && timings.length) {
                  return (
                    <div className="space-y-1">
                      {timings.map((day: DayTiming) => (
                        <div
                          key={day.day_id}
                          className={cn(
                            "flex items-start justify-between p-3 rounded-md",
                            day.day_id === apiToday && "bg-amber-100"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <CalendarDays size={16} className="text-gray-500" />
                            <span
                              className={cn(
                                "text-base",
                                day.day_id === apiToday && "font-medium"
                              )}
                            >
                              {day.name}
                            </span>
                          </div>

                          <div className="text-right">
                            {day.timings && day.timings.length > 0 ? (
                              day.timings.map(
                                (time: TimingSlot, index: number) => (
                                  <div
                                    key={time.time_id || index}
                                    className={cn(
                                      "text-sm text-gray-600",
                                      index > 0 && "mt-1",
                                      day.day_id === apiToday &&
                                        "font-bold text-gray-900"
                                    )}
                                  >
                                    {formatOpeningHours(time.start_time)} -{" "}
                                    {formatOpeningHours(time.end_time)}
                                  </div>
                                )
                              )
                            ) : (
                              <span className="text-sm text-gray-600">
                                Đóng cửa
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                }

                // Fallback if no data found
                return (
                  <div className="p-4 text-center text-gray-500 flex flex-col items-center gap-2">
                    <CloudCog className="h-8 w-8 text-gray-400" />
                    <div>Không có thông tin giờ mở cửa</div>
                    <div className="text-xs text-gray-400">
                      {JSON.stringify(timingsData)?.substring(0, 100)}...
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
